import { useInfiniteQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { api, query } from '../../api/client'
import type { Review } from '../../api/types'
import { useMeta } from '../../hooks/useMeta'
import { formatDate } from '../../lib/format'

type Page = { reviews: Review[]; nextCursor: string | null }

export function ReviewsPanel({ appId }: { appId: number }) {
	const { data: meta } = useMeta()
	const [storefront, setStorefront] = useState('')
	const [rating, setRating] = useState('')
	const reviews = useInfiniteQuery({
		queryKey: ['reviews', appId, storefront, rating],
		queryFn: ({ pageParam }) =>
			api.get<Page>(`/api/v1/apps/${appId}/reviews${query({ storefront, rating, cursor: pageParam, limit: 20 })}`),
		initialPageParam: undefined as string | undefined,
		getNextPageParam: (last) => last.nextCursor ?? undefined,
	})
	const items = reviews.data?.pages.flatMap((p) => p.reviews) ?? []
	return (
		<section className="card stack">
			<div className="row" style={{ justifyContent: 'space-between' }}>
				<h2 style={{ margin: 0 }}>Reviews</h2>
				<div className="row">
					<select aria-label="Review country" value={storefront} onChange={(e) => setStorefront(e.target.value)}>
						<option value="">All countries</option>
						{meta?.storefronts.map((s) => (
							<option key={s.code} value={s.code}>
								{s.name}
							</option>
						))}
					</select>
					<select aria-label="Stars" value={rating} onChange={(e) => setRating(e.target.value)}>
						<option value="">All ratings</option>
						{[5, 4, 3, 2, 1].map((n) => (
							<option key={n} value={n}>
								{'★'.repeat(n)}
							</option>
						))}
					</select>
				</div>
			</div>
			{items.length === 0 && (
				<p className="muted">
					{reviews.isPending
						? 'Loading…'
						: 'No reviews collected. Reviews are gathered for top-ranked and watchlisted apps.'}
				</p>
			)}
			{items.map((r) => (
				<article key={r.id} style={{ borderTop: '1px solid var(--border)', paddingTop: '0.75rem' }}>
					<div className="row" style={{ justifyContent: 'space-between' }}>
						<strong>
							<span aria-label={`${r.rating} stars`} style={{ color: '#d97706' }}>
								{'★'.repeat(r.rating)}
								<span style={{ color: 'var(--border)' }}>{'★'.repeat(5 - r.rating)}</span>
							</span>{' '}
							{r.title}
						</strong>
						<span className="muted small">
							{r.storefront.toUpperCase()} · {formatDate(r.reviewedAt)}
							{r.version ? ` · v${r.version}` : ''}
						</span>
					</div>
					<p style={{ margin: '0.35rem 0', whiteSpace: 'pre-line' }}>{r.body}</p>
					<span className="muted small">{r.author}</span>
				</article>
			))}
			{reviews.hasNextPage && (
				<button type="button" onClick={() => reviews.fetchNextPage()} disabled={reviews.isFetchingNextPage}>
					{reviews.isFetchingNextPage ? 'Loading…' : 'Load more'}
				</button>
			)}
		</section>
	)
}
